import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { getTokens } from "../utils/auth";
import {
  listCategoriesApiCategoriesGet,
  getCategoryApiCategoriesCategoryIdGet,
  createCategoryApiCategoriesPost,
  updateCategoryApiCategoriesCategoryIdPatch,
  deleteCategoryApiCategoriesCategoryIdDelete,
  listSubcategoriesApiCategoriesSubcategoriesGet,
  getSubcategoryApiCategoriesSubcategoriesSubcategoryIdGet,
  createSubcategoryApiCategoriesSubcategoriesPost,
  updateSubcategoryApiCategoriesSubcategoriesSubcategoryIdPatch,
  deleteSubcategoryApiCategoriesSubcategoriesSubcategoryIdDelete,
} from "../api/generated";
import type {
  CategoryListResponse,
  CategoryCreate,
  CategoryUpdate,
  SubcategoryListResponse,
  SubcategoryCreate,
  SubcategoryUpdate,
  ListCategoriesApiCategoriesGetResponse,
  GetCategoryApiCategoriesCategoryIdGetResponse,
  ListSubcategoriesApiCategoriesSubcategoriesGetResponse,
  GetSubcategoryApiCategoriesSubcategoriesSubcategoryIdGetResponse,
} from "../api/generated/types.gen";

export interface CategoryFilters {
  include_inactive?: boolean;
}

export interface SubcategoryFilters {
  category_id?: string;
  include_inactive?: boolean;
}

/**
 * Hook for fetching list of categories.
 */
export function useCategories(filters: CategoryFilters = {}) {
  return useQuery<ListCategoriesApiCategoriesGetResponse>({
    queryKey: ["categories", filters],
    queryFn: async () => {
      const response = await listCategoriesApiCategoriesGet({
        query: {
          include_inactive: filters.include_inactive,
        },
      });

      if (!response.data) {
        throw new Error("Failed to fetch categories");
      }

      return response.data;
    },
  });
}

/**
 * Hook for fetching a single category by ID.
 */
export function useCategory(categoryId: string | null) {
  return useQuery<GetCategoryApiCategoriesCategoryIdGetResponse>({
    queryKey: ["category", categoryId],
    queryFn: async () => {
      if (!categoryId) {
        throw new Error("Category ID is required");
      }

      const response = await getCategoryApiCategoriesCategoryIdGet({
        path: { category_id: categoryId },
      });

      if (!response.data) {
        throw new Error("Failed to fetch category");
      }

      return response.data;
    },
    enabled: !!categoryId,
  });
}

/**
 * Hook for creating a new category.
 */
export function useCreateCategory() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (data: CategoryCreate) => {
      const { accessToken } = getTokens();

      if (!accessToken) {
        throw new Error("No access token found");
      }

      const response = await createCategoryApiCategoriesPost({
        body: data,
      });

      if (!response.data) {
        throw new Error("Failed to create category");
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      enqueueSnackbar("Category created successfully", { variant: "success" });
    },
    onError: (error) => {
      enqueueSnackbar(
        error instanceof Error ? error.message : "Failed to create category",
        { variant: "error" }
      );
    },
  });
}

/**
 * Hook for updating a category.
 */
export function useUpdateCategory() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      categoryId,
      data,
    }: {
      categoryId: string;
      data: CategoryUpdate;
    }) => {
      const { accessToken } = getTokens();

      if (!accessToken) {
        throw new Error("No access token found");
      }

      const response = await updateCategoryApiCategoriesCategoryIdPatch({
        path: { category_id: categoryId },
        body: data,
      });

      if (!response.data) {
        throw new Error("Failed to update category");
      }

      return response.data;
    },
    onSuccess: (data, { categoryId }) => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["category", categoryId] });
      enqueueSnackbar("Category updated successfully", { variant: "success" });
    },
    onError: (error) => {
      enqueueSnackbar(
        error instanceof Error ? error.message : "Failed to update category",
        { variant: "error" }
      );
    },
  });
}

/**
 * Hook for deleting a category.
 */
export function useDeleteCategory() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (categoryId: string) => {
      const { accessToken } = getTokens();

      if (!accessToken) {
        throw new Error("No access token found");
      }

      const response = await deleteCategoryApiCategoriesCategoryIdDelete({
        path: { category_id: categoryId },
      });

      return response.data;
    },
    onMutate: async (categoryId) => {
      // Optimistically remove from UI
      await queryClient.cancelQueries({ queryKey: ["categories"] });

      const previousCategories = queryClient.getQueryData<CategoryListResponse>(
        ["categories"]
      );

      queryClient.setQueriesData<CategoryListResponse>(
        { queryKey: ["categories"] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter((cat) => cat.id !== categoryId),
            total: old.total - 1,
          };
        }
      );

      return { previousCategories };
    },
    onSuccess: () => {
      enqueueSnackbar("Category deleted successfully", { variant: "success" });
    },
    onError: (_err, _categoryId, context) => {
      // Rollback on error
      if (context?.previousCategories) {
        queryClient.setQueryData(["categories"], context.previousCategories);
      }
      enqueueSnackbar("Failed to delete category", { variant: "error" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

/**
 * Hook for fetching list of subcategories.
 */
export function useSubcategories(filters: SubcategoryFilters = {}) {
  return useQuery<ListSubcategoriesApiCategoriesSubcategoriesGetResponse>({
    queryKey: ["subcategories", filters],
    queryFn: async () => {
      const response = await listSubcategoriesApiCategoriesSubcategoriesGet({
        query: {
          category_id: filters.category_id,
          include_inactive: filters.include_inactive,
        },
      });

      if (!response.data) {
        throw new Error("Failed to fetch subcategories");
      }

      return response.data;
    },
  });
}

/**
 * Hook for fetching a single subcategory by ID.
 */
export function useSubcategory(subcategoryId: string | null) {
  return useQuery<GetSubcategoryApiCategoriesSubcategoriesSubcategoryIdGetResponse>(
    {
      queryKey: ["subcategory", subcategoryId],
      queryFn: async () => {
        if (!subcategoryId) {
          throw new Error("Subcategory ID is required");
        }

        const response =
          await getSubcategoryApiCategoriesSubcategoriesSubcategoryIdGet({
            path: { subcategory_id: subcategoryId },
          });

        if (!response.data) {
          throw new Error("Failed to fetch subcategory");
        }

        return response.data;
      },
      enabled: !!subcategoryId,
    }
  );
}

/**
 * Hook for creating a new subcategory.
 */
export function useCreateSubcategory() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (data: SubcategoryCreate) => {
      const { accessToken } = getTokens();

      if (!accessToken) {
        throw new Error("No access token found");
      }

      const response = await createSubcategoryApiCategoriesSubcategoriesPost({
        body: data,
      });

      if (!response.data) {
        throw new Error("Failed to create subcategory");
      }

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["subcategories"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      enqueueSnackbar("Subcategory created successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueSnackbar(
        error instanceof Error ? error.message : "Failed to create subcategory",
        { variant: "error" }
      );
    },
  });
}

/**
 * Hook for updating a subcategory.
 */
export function useUpdateSubcategory() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      subcategoryId,
      data,
    }: {
      subcategoryId: string;
      data: SubcategoryUpdate;
    }) => {
      const { accessToken } = getTokens();

      if (!accessToken) {
        throw new Error("No access token found");
      }

      const response =
        await updateSubcategoryApiCategoriesSubcategoriesSubcategoryIdPatch({
          path: { subcategory_id: subcategoryId },
          body: data,
        });

      if (!response.data) {
        throw new Error("Failed to update subcategory");
      }

      return response.data;
    },
    onSuccess: (data, { subcategoryId }) => {
      queryClient.invalidateQueries({ queryKey: ["subcategories"] });
      queryClient.invalidateQueries({
        queryKey: ["subcategory", subcategoryId],
      });
      enqueueSnackbar("Subcategory updated successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueSnackbar(
        error instanceof Error ? error.message : "Failed to update subcategory",
        { variant: "error" }
      );
    },
  });
}

/**
 * Hook for deleting a subcategory.
 */
export function useDeleteSubcategory() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (subcategoryId: string) => {
      const { accessToken } = getTokens();

      if (!accessToken) {
        throw new Error("No access token found");
      }

      const response =
        await deleteSubcategoryApiCategoriesSubcategoriesSubcategoryIdDelete({
          path: { subcategory_id: subcategoryId },
        });

      return response.data;
    },
    onMutate: async (subcategoryId) => {
      // Optimistically remove from UI
      await queryClient.cancelQueries({ queryKey: ["subcategories"] });

      const previousSubcategories =
        queryClient.getQueryData<SubcategoryListResponse>(["subcategories"]);

      queryClient.setQueriesData<SubcategoryListResponse>(
        { queryKey: ["subcategories"] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter((sub) => sub.id !== subcategoryId),
            total: old.total - 1,
          };
        }
      );

      return { previousSubcategories };
    },
    onSuccess: () => {
      enqueueSnackbar("Subcategory deleted successfully", {
        variant: "success",
      });
    },
    onError: (_err, _subcategoryId, context) => {
      // Rollback on error
      if (context?.previousSubcategories) {
        queryClient.setQueryData(
          ["subcategories"],
          context.previousSubcategories
        );
      }
      enqueueSnackbar("Failed to delete subcategory", { variant: "error" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["subcategories"] });
    },
  });
}
